import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { usePermission } from '../hooks/usePermission';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { FIELD } from '../components/ui/tokens';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { EmptyState, ErrorState, LoadingIndicator } from '../components/ui/States';
import {
  cancelPaymentRun,
  getPaymentRun,
  getPaymentRunCandidates,
  paymentRunRefusalMessage,
  recordPaymentRun,
  updatePaymentRun,
} from '../lib/paymentRunsApi';
import type { Candidates, PaymentRun, RecordResult } from '../lib/paymentRunsApi';
import { downloadObjectsCsv } from '../utils/downloadCsv';
import { formatDateTime } from '../utils/dateFormat';
import { PAYMENT_METHODS } from '../utils/paymentOnReceipt';
import { browserLatestPaymentDay } from '../features/vendorInvoices/paymentOnReceiptForm';
import { formatReportDay, formatReportMoney } from '../features/accountsPayableReports/format';
import { RunSection } from '../features/paymentRuns/RunSection';
import { RunGroupsTable } from '../features/paymentRuns/RunGroupsTable';
import { RecordOutcomePanel } from '../features/paymentRuns/RecordOutcomePanel';
import {
  BANK_LIST_HEADERS,
  RUN_STATUS_LABEL,
  RUN_STATUS_TONE,
  bankListFileName,
  bankListRows,
  moneyByCurrency,
  remainingByCurrency,
} from '../features/paymentRuns/runDisplay';
import {
  buildSections,
  editSignature,
  hasProblems,
  mergeCandidates,
  referenceEdits,
  selectionLines,
  selectionTotals,
} from '../features/paymentRuns/runEditing';
import type { EditHeader, EditSection } from '../features/paymentRuns/runEditing';
import DateInput from "../components/DateInput";

/**
 * One payment run (D49 phase 4). A draft (or a run with some suppliers not yet recorded) is the
 * editor: tick or untick bills, part-pay one, change a supplier's reference, choose the date and
 * method, save. "Record payments" is offered only when what is on screen is what is saved, so
 * what gets recorded is exactly what was reviewed. Each supplier is its own all-or-nothing on the
 * server; the outcome panel says which were recorded and which were not, and why.
 *
 * Everything shown about money is the server's. The page sends only which bills and how much.
 */

const LABEL = 'mb-1 block text-xs font-medium text-slate-600';
// Same idea as the Accounts page's quick Pay window (2026-09-22): a required/optional hint painted
// by CSS `::after`, so it never becomes part of the label's own text and `getByLabelText` still
// matches the plain word ("Payment date", not "Payment date *").
const REQUIRED = "after:ml-1 after:text-rose-600 after:content-['*']";
const OPTIONAL = "after:ml-1 after:font-normal after:text-slate-400 after:content-['(optional)']";

const headerOf = (run: PaymentRun): EditHeader => ({
  dueBy: run.dueBy || '',
  paymentDate: run.paymentDate || '',
  method: run.method || '',
  note: run.note || '',
});

export const PaymentRunPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id = '' } = useParams();
  // Saving, changing, cancelling and recording a run need the Financials CREATE right (the same
  // gate as recording one payment); without it the run is read-only.
  const { canCreate } = usePermission('financials');

  const [run, setRun] = useState<PaymentRun | null>(null);
  const [candidates, setCandidates] = useState<Candidates | null>(null);
  const [candidateProblem, setCandidateProblem] = useState('');
  const [sections, setSections] = useState<EditSection[]>([]);
  const [header, setHeader] = useState<EditHeader>({ dueBy: '', paymentDate: '', method: '', note: '' });
  // D109.6: a refused typed date is not emitted, so `header` still holds the old one. Saving would store it.
  const [dueByValid, setDueByValid] = useState(true);
  const [paymentDateValid, setPaymentDateValid] = useState(true);
  const datesInvalid = !dueByValid || !paymentDateValid;
  const [includeUndated, setIncludeUndated] = useState(false);
  const [savedSignature, setSavedSignature] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState<'' | 'saving' | 'recording' | 'cancelling'>('');
  const [message, setMessage] = useState('');
  const [outcome, setOutcome] = useState<RecordResult[] | null>(null);
  const [reloads, setReloads] = useState(0);
  const latest = useRef(0);

  const adopt = useCallback((next: PaymentRun, cands: Candidates | null) => {
    const nextSections = buildSections(next, cands);
    const nextHeader = headerOf(next);
    setRun(next);
    setSections(nextSections);
    setHeader(nextHeader);
    setSavedSignature(editSignature(nextSections, nextHeader));
  }, []);

  // Load the run, then (when it can still change) the bills due by its date.
  useEffect(() => {
    if (!id) return;
    latest.current += 1;
    const mine = latest.current;
    setStatus('loading');
    setNotFound(false);
    (async () => {
      try {
        const loaded = await getPaymentRun(id);
        let cands: Candidates | null = null;
        let problem = '';
        // Only someone who can change the run is offered more bills to pick from.
        if (loaded.editable && loaded.dueBy && canCreate) {
          try {
            cands = await getPaymentRunCandidates({ dueBy: loaded.dueBy, excludeRun: loaded.id });
          } catch (err) {
            // The saved bills are still shown; only the offer of more is missing.
            problem = paymentRunRefusalMessage(err);
          }
        }
        if (mine !== latest.current) return;
        setCandidates(cands);
        setCandidateProblem(problem);
        adopt(loaded, cands);
        setStatus('ready');
      } catch (err) {
        if (mine !== latest.current) return;
        setNotFound((err as { status?: number })?.status === 404);
        setStatus('error');
      }
    })();
  }, [id, reloads, adopt, canCreate]);

  const signature = useMemo(() => editSignature(sections, header), [sections, header]);
  const editing = Boolean(run?.editable) && canCreate;
  const dirty = editing ? signature !== savedSignature : false;
  const problems = hasProblems(sections);
  const totals = useMemo(() => selectionTotals(sections), [sections]);
  const unrecorded = run ? run.groups.filter((g) => g.status !== 'recorded') : [];
  const recorded = run ? run.groups.filter((g) => g.status === 'recorded') : [];
  const locked = busy !== '' || Boolean(run?.recording);

  const changeRow = (billId: string, change: (row: EditSection['rows'][number]) => EditSection['rows'][number]) =>
    setSections((prev) => prev.map((s) => ({ ...s, rows: s.rows.map((r) => (r.billId === billId ? change(r) : r)) })));

  /** The bills due by a new day (or with the undated ones added), keeping what is ticked. */
  const reloadCandidates = async (dueBy: string, withUndated: boolean) => {
    if (!run || !dueBy) return;
    try {
      const cands = await getPaymentRunCandidates({ dueBy, includeUndated: withUndated, excludeRun: run.id });
      setCandidates(cands);
      setCandidateProblem('');
      setSections((prev) => mergeCandidates(prev, cands));
    } catch (err) {
      setCandidateProblem(paymentRunRefusalMessage(err));
    }
  };

  const save = async (): Promise<PaymentRun | null> => {
    if (!run) return null;
    if (datesInvalid) {
      setMessage('Fix the date first: a date that is not a real day cannot be saved.');
      return null;
    }
    setBusy('saving');
    setMessage('');
    try {
      const next = await updatePaymentRun(run.id, {
        dueBy: header.dueBy,
        paymentDate: header.paymentDate || null,
        method: header.method,
        note: header.note,
        lines: selectionLines(sections),
        groups: referenceEdits(sections),
      });
      adopt(next, candidates);
      return next;
    } catch (err) {
      setMessage(paymentRunRefusalMessage(err));
      return null;
    } finally {
      setBusy('');
    }
  };

  const record = async () => {
    if (!run) return;
    const count = unrecorded.length;
    const ok = await confirm({
      title: `Record ${count} ${count === 1 ? 'payment' : 'payments'}?`,
      // The server's own "still to record" figure, per currency: nothing is re-added here.
      summary: `${remainingByCurrency(run.totals)}, dated ${formatReportDay(run.paymentDate)}, paid by ${run.method}.`,
      hint:
        'Each supplier is recorded on its own. If one cannot be recorded the others still are, and you will see which. ' +
        'A recorded payment can only be undone by voiding it from its payment voucher.',
      confirmLabel: 'Record payments',
    });
    if (!ok) return;
    setBusy('recording');
    setMessage('');
    setOutcome(null);
    try {
      const done = await recordPaymentRun(run.id);
      setOutcome(done.results);
      adopt(done.run, candidates);
    } catch (err) {
      setMessage(paymentRunRefusalMessage(err));
    } finally {
      setBusy('');
    }
  };

  const cancel = async () => {
    if (!run) return;
    const ok = await confirm({
      title: `Cancel ${run.runNumber}?`,
      summary: 'Nothing has been paid. The run is kept, marked cancelled, and its bills can be picked again.',
      confirmLabel: 'Cancel this run',
      cancelLabel: 'Keep it',
      tone: 'error',
    });
    if (!ok) return;
    setBusy('cancelling');
    setMessage('');
    try {
      adopt(await cancelPaymentRun(run.id), null);
    } catch (err) {
      setMessage(paymentRunRefusalMessage(err));
    } finally {
      setBusy('');
    }
  };

  const exportBankList = () => {
    if (run) downloadObjectsCsv(bankListFileName(run), bankListRows(run), BANK_LIST_HEADERS);
  };

  const back = (
    <Button
      variant="secondary"
      icon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
      onClick={() => navigate('/accounts/payment-runs')}
    >
      Back
    </Button>
  );

  // What "Record payments" is waiting for, in words.
  const recordBlocker = !run
    ? ''
    : datesInvalid
      ? 'Fix the date first: it is not a real day.'
    : dirty
      ? 'Save your changes first: what is recorded is what is saved.'
      : problems
        ? 'Fix the amounts marked in red.'
        : !header.paymentDate
          ? 'Choose the payment date.'
          : !header.method
            ? 'Choose how the suppliers are paid.'
            : unrecorded.length === 0
              ? 'There is nothing left to record.'
              : '';

  if (status === 'loading') {
    return (
      <div className="flex h-full flex-col bg-brand-bg">
        <PageHeader back={back} title="Payment run" />
        <main className="px-4 py-4 sm:px-8">
          <LoadingIndicator label="Loading the payment run..." />
        </main>
      </div>
    );
  }

  if (status === 'error' || !run) {
    return (
      <div className="flex h-full flex-col bg-brand-bg">
        <PageHeader back={back} title="Payment run" />
        <main className="px-4 py-4 sm:px-8">
          {notFound ? (
            <EmptyState title="This payment run could not be found." detail="It may have been mistyped in the address." />
          ) : (
            <ErrorState message="The payment run could not be loaded." onRetry={() => setReloads((n) => n + 1)} />
          )}
        </main>
      </div>
    );
  }

    return (
    <div className="flex h-full flex-col overflow-auto bg-brand-bg">
      <PageHeader
        back={back}
        title={
          <span className="flex items-center gap-2">
            {run.runNumber}
            <span className={`rounded border px-2 py-0.5 text-xs font-medium ${RUN_STATUS_TONE[run.status]}`}>
              {RUN_STATUS_LABEL[run.status]}
            </span>
          </span>
        }
        subtitle={
          // 2026-09-22 QA feedback ("I don't get the point of that"): say up front when to reach
          // for a run at all, since paying one bill has its own one-click path now.
          `Use this to pay several suppliers in one go. For a single bill, use Pay on the Accounts list instead. ` +
          `Created by ${run.createdBy || 'unknown'} on ${formatDateTime(run.createdAt, '--')}${
            run.recordedAt ? `, recorded by ${run.recordedBy || 'unknown'} on ${formatDateTime(run.recordedAt, '--')}` : ''
          }.`
        }
        actions={
          <>
            <Button variant="secondary" icon={<Download aria-hidden="true" className="h-4 w-4" />} onClick={exportBankList} disabled={!run.groups.length}>
              Bank list (CSV)
            </Button>
            {editing && run.status === 'draft' ? (
              <Button variant="secondary" onClick={cancel} disabled={locked}>
                Cancel run
              </Button>
            ) : null}
            {editing ? (
              <Button variant="secondary" onClick={() => void save()} disabled={locked || !dirty || problems || datesInvalid} loading={busy === 'saving'}>
                Save changes
              </Button>
            ) : null}
            {editing ? (
              <Button variant="primary" onClick={() => void record()} disabled={locked || recordBlocker !== ''} loading={busy === 'recording'}>
                Record payments
              </Button>
            ) : null}
          </>
        }
      />

      <main className="flex-1 space-y-4 px-4 py-4 sm:px-8">
        {message ? (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {message}
          </p>
        ) : null}

        {run.recording ? (
          <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This run is being recorded right now. Reload in a moment to see the result.
          </p>
        ) : null}

        {outcome ? <RecordOutcomePanel run={run} results={outcome} onDismiss={() => setOutcome(null)} /> : null}

        {editing ? (
          <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-3">
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">1. Which bills</h2>
              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="run-due-by" className={`${LABEL} ${REQUIRED}`}>Bills due by</label>
                  <DateInput
                    id="run-due-by"
                    value={header.dueBy}
                    disabled={locked}
                    validity={setDueByValid}
                    onChange={(v) => {
                      setHeader((h) => ({ ...h, dueBy: v }));
                      void reloadCandidates(v, includeUndated);
                    }}
                    className={`${FIELD} h-9 w-full border-slate-300 bg-white`}
                  />
                </div>
                <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={includeUndated}
                    disabled={locked}
                    onChange={(e) => {
                      setIncludeUndated(e.target.checked);
                      void reloadCandidates(header.dueBy, e.target.checked);
                    }}
                  />
                  Also show bills with no due date
                </label>
              </div>
            </div>
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">2. How you're paying</h2>
              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-3">
                <div>
                  <label htmlFor="run-payment-date" className={`${LABEL} ${REQUIRED}`}>Payment date</label>
                  <DateInput
                    id="run-payment-date"
                    value={header.paymentDate}
                    max={browserLatestPaymentDay()}
                    disabled={locked}
                    validity={setPaymentDateValid}
                    onChange={(v) => setHeader((h) => ({ ...h, paymentDate: v }))}
                    className={`${FIELD} h-9 w-full border-slate-300 bg-white`}
                  />
                </div>
                <div>
                  <label htmlFor="run-method" className={`${LABEL} ${REQUIRED}`}>Paid by</label>
                  <select
                    id="run-method"
                    value={header.method}
                    disabled={locked}
                    onChange={(e) => setHeader((h) => ({ ...h, method: e.target.value }))}
                    className={`${FIELD} h-9 w-full border-slate-300 bg-white`}
                  >
                    <option value="" disabled>Choose a method</option>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="run-note" className={`${LABEL} ${OPTIONAL}`}>Note</label>
                  <input
                    id="run-note"
                    value={header.note}
                    disabled={locked}
                    maxLength={500}
                    onChange={(e) => setHeader((h) => ({ ...h, note: e.target.value }))}
                    className={`${FIELD} h-9 w-full border-slate-300 bg-white`}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
            <dt className="font-medium text-slate-500">Bills due by</dt>
            <dd>{formatReportDay(run.dueBy)}</dd>
            <dt className="font-medium text-slate-500">Payment date</dt>
            <dd>{formatReportDay(run.paymentDate)}</dd>
            <dt className="font-medium text-slate-500">Paid by</dt>
            <dd>{run.method || '--'}</dd>
            {run.note ? (
              <>
                <dt className="font-medium text-slate-500">Note</dt>
                <dd>{run.note}</dd>
              </>
            ) : null}
            <dt className="font-medium text-slate-500">Total</dt>
            <dd>{moneyByCurrency(run.totals)}</dd>
          </dl>
        )}

        {candidateProblem ? (
          <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {candidateProblem} The bills already in the run are still shown.
          </p>
        ) : null}

        {editing ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2" data-testid="selection-summary">
              <p className="text-sm text-slate-700">
                {totals.length
                  ? totals
                      .map((t) => `${formatReportMoney(t.amount, t.currency)} to ${t.suppliers} ${t.suppliers === 1 ? 'supplier' : 'suppliers'}`)
                      .join(', ')
                  : 'Nothing is ticked.'}
                {recorded.length ? ` Still to record: ${remainingByCurrency(run.totals)}.` : ''}
              </p>
              {dirty ? <p className="text-xs font-medium text-amber-800">You have unsaved changes.</p> : null}
            </div>
            {recordBlocker && !dirty ? <p className="text-xs text-slate-500">{recordBlocker}</p> : null}

            {sections.length ? (
              <div className="space-y-3">
                {sections.map((section) => (
                  <RunSection
                    key={section.key}
                    section={section}
                    disabled={locked}
                    onToggle={(billId) => changeRow(billId, (r) => ({ ...r, ticked: !r.ticked }))}
                    onAmount={(billId, text) => changeRow(billId, (r) => ({ ...r, amount: text }))}
                    onReference={(text) =>
                      setSections((prev) => prev.map((s) => (s.key === section.key ? { ...s, reference: text } : s)))
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No bills to show."
                detail="No open bills are due by that date. Choose a later date, or show bills with no due date."
              />
            )}
          </>
        ) : null}

        {editing && recorded.length ? (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-900">Recorded</h2>
            <RunGroupsTable
              groups={recorded}
              caption={`Payments recorded by ${run.runNumber}`}
              onOpenVoucher={(paymentId) => navigate(`/accounts/payments/${paymentId}`)}
            />
          </div>
        ) : null}

        {!editing ? (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-slate-900">Suppliers in this run</h2>
            <RunGroupsTable
              groups={run.groups}
              caption={`Suppliers in ${run.runNumber}`}
              onOpenVoucher={(paymentId) => navigate(`/accounts/payments/${paymentId}`)}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default PaymentRunPage;
